import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { map } from 'rxjs';
import { Observable } from 'rxjs';
import {
  Assignment,
  AssignmentMontage,
  AssignmentService,
  AssignmentServiceDossier,
  CalculationPriceRulesResponse,
  CalculationPriceRule,
  ComplaintDossiersResponse,
  ComplaintRecord,
  Invoice,
} from '@api/models/assignment.model';
import { IndexResponse, ShowResponse } from '@api/models/response.model';
import { AppService } from '../../app.service';

@Injectable({
  providedIn: 'root',
})
export class AssignmentsService {
  private http = inject(HttpClient);
  private appService = inject(AppService);

  constructor() {}

  getAssignments(
    page: number = 1,
    perPage: number = 50,
    filters: Record<string, string> = {}
  ): Observable<IndexResponse<Assignment>> {
    const municipalityId = this.appService.municipalityId();
    if (!municipalityId) {
      throw new Error('Geen gemeente geselecteerd');
    }

    // Convert filters object to query parameters
    const filterParams = Object.entries(filters)
      .filter(([_, value]) => value) // Only include non-empty values
      .map(([key, value]) => `filter[${key}]=${encodeURIComponent(value)}`)
      .join('&');

    return this.http
      .get<IndexResponse<Assignment>>(
        `${environment.apiUrl}/municipalities/${municipalityId}/assignments?page=${page}&per_page=${perPage}${filterParams ? '&' + filterParams : ''}`
      )
      .pipe(map((response) => response));
  }

  getAssignment(assignmentId: string): Observable<Assignment> {
    return this.http
      .get<ShowResponse<Assignment>>(
        `${environment.apiUrl}/assignments/${assignmentId}`
      )
      .pipe(map((response) => response.data));
  }

  getAssignmentMontages(assignmentId: string): Observable<AssignmentMontage[]> {
    return this.http.get<AssignmentMontage[]>(
      `${environment.apiUrl}/assignments/${assignmentId}/montage`
    );
  }

  getAssignmentService(assignmentId: string): Observable<AssignmentService[]> {
    return this.http.get<AssignmentService[]>(
      `${environment.apiUrl}/assignments/${assignmentId}/service`
    );
  }

  getAssignmentServiceDossier(assignmentId: string): Observable<AssignmentServiceDossier> {
    return this.http.get<AssignmentServiceDossier>(
      `${environment.apiUrl}/assignments/${assignmentId}/service-dossiers`
    );
  }

  getCalculationPriceRules(
    assignmentId: string
  ): Observable<CalculationPriceRulesResponse> {
    const params = new URLSearchParams({
      artikelgroep: '8140',
    });

    return this.http
      .get<
        | CalculationPriceRulesResponse
        | ShowResponse<CalculationPriceRulesResponse>
        | CalculationPriceRule[]
      >(
        `${environment.apiUrl}/assignments/${assignmentId}/calculation-price-rules?${params}`
      )
      .pipe(map((response) => this.unwrapPriceRules(response)));
  }

  private unwrapPriceRules(
    response:
      | CalculationPriceRulesResponse
      | ShowResponse<CalculationPriceRulesResponse>
      | CalculationPriceRule[]
  ): CalculationPriceRulesResponse {
    let body: unknown = response;

    if (
      response &&
      typeof response === 'object' &&
      'data' in response &&
      (response as ShowResponse<unknown>).data != null
    ) {
      body = (response as ShowResponse<unknown>).data;
    }

    if (Array.isArray(body)) {
      return { price_rules: body };
    }

    const rules = (body as CalculationPriceRulesResponse)?.price_rules;
    return {
      price_rules: Array.isArray(rules) ? rules : [],
    };
  }

  getAssignmentComplaintDossiers(
    assignmentId: string,
    personID: string
  ): Observable<ComplaintDossiersResponse> {
    return this.http
      .get<
        | ComplaintDossiersResponse
        | ShowResponse<ComplaintDossiersResponse>
        | ComplaintRecord[]
      >(
        `${environment.apiUrl}/assignments/${assignmentId}/complaint-dossiers?personID=${encodeURIComponent(personID)}`
      )
      .pipe(map((response) => this.unwrapComplaintDossiers(response)));
  }

  /** API kan `{ data: ... }`, een plat object of direct een array teruggeven. */
  private unwrapComplaintDossiers(
    response:
      | ComplaintDossiersResponse
      | ShowResponse<ComplaintDossiersResponse>
      | ComplaintRecord[]
  ): ComplaintDossiersResponse {
    let body: unknown = response;

    if (
      response &&
      typeof response === 'object' &&
      'data' in response &&
      (response as ShowResponse<unknown>).data != null
    ) {
      body = (response as ShowResponse<unknown>).data;
    }

    if (Array.isArray(body)) {
      return { complaint_records: body };
    }

    const records = (body as ComplaintDossiersResponse)?.complaint_records;
    return {
      complaint_records: Array.isArray(records) ? records : [],
    };
  }

  downloadFile(assignmentId: string, subjectId: string, fileId: string) {
    return this.http.get(
      `${environment.apiUrl}/assignments/${assignmentId}/files/${subjectId}/${fileId}`,
      {
        responseType: 'arraybuffer', // Belangrijk voor binaire data
      }
    );
  }

  getInvoices(assignmentId: string, personID: string): Observable<Invoice[]> {
    return this.http
      .get<IndexResponse<Invoice>>(
        `${environment.apiUrl}/assignments/${assignmentId}/invoices?personID=${personID}`
      )
      .pipe(map((response) => response.data));
  }

  downloadInvoice(assignmentId: string, invoiceNumber: string, personID: string) {
    return this.http.get(
      `${environment.apiUrl}/assignments/${assignmentId}/invoices/${invoiceNumber}?personID=${personID}`,
      {
        responseType: 'arraybuffer',
      }
    );
  }
}
